import React, { useState, useMemo, useEffect } from 'react';
import { X, ArrowRight, CheckCircle2, ShieldCheck, Database, Bot, MapPin, Cloud, Layers } from 'lucide-react';
import { mockContacts } from '../../mockData';
import { BitumenGrade } from '../../types';

interface StandardizationModalProps {
  isOpen: boolean;
  onClose: () => void;
  data: any | null;
  onApply: (updated: any) => void;
}

// Suffix normalization map for entities
const SUFFIX_MAP: Record<string, string> = {
  'LIMITED': 'LTD',
  'CORPORATION': 'CORP',
  'INCORPORATED': 'INC',
  'COMPANY': 'CO',
  'AND': '&',
  'INTERNATIONAL': 'INT'
};

// Master registry of known internal entities
const MASTER_ENTITIES: Record<string, { aliases: string[], address?: string }> = {
  'SHIV INTERNATIONAL LTD': { 
    aliases: ['SHIV', 'SHIV INT', 'SHIV UGANDA', 'SHIV TZ', 'SHIV INTERNATIONAL', 'M/S SHIV', 'SHIV INT LTD'],
    address: 'Plot 12, Industrial Area, Kampala'
  },
  'MEGABUILD CONTRACTORS LTD': {
    aliases: ['MEGABUILD', 'MEGA BUILD', 'MB CONTRACTORS', 'MEGA-BUILD', 'MEGABUILD TANZANIA'],
    address: 'Plot 45, Bagamoyo Road, Dar es Salaam'
  },
  'TANROADS': {
    aliases: ['TANROAD', 'TR', 'TANZANIA ROADS AGENCY', 'TAN ROADS', 'TANROADS HQ'],
    address: 'Morogoro Road, Dar es Salaam'
  },
  'ALKIN': {
    aliases: ['ALKIN PETRO', 'ALKIN SUPPLY', 'ALKIN G.T', 'ALKIN GENERAL TRADING', 'ALKIN DUBAI'],
    address: 'United Arab Emirates'
  },
  'CHINA CIVIL ENGINEERING CONSTRUCTION CORP': {
      aliases: ['CCECC', 'CCECC ZAMBIA', 'CCECC TZ', 'CHINA CIVIL'],
      address: 'Beijing, China'
  }
};

const normalizeText = (text: string): string => {
  if (!text) return '';
  let n = text.toUpperCase().replace(/[.,]/g, '').trim();
  Object.keys(SUFFIX_MAP).forEach(suffix => {
    const regex = new RegExp(`\\b${suffix}\\b`, 'g');
    n = n.replace(regex, SUFFIX_MAP[suffix]);
  });
  return n.replace(/\s+/g, ' ');
};

const getLevenshteinDistance = (a: string, b: string) => {
  const matrix = Array.from({ length: a.length + 1 }, () => Array(b.length + 1).fill(0));
  for (let i = 0; i <= a.length; i++) matrix[i][0] = i;
  for (let j = 0; j <= b.length; j++) matrix[0][j] = j;
  for (let i = 1; i <= a.length; i++) {
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      matrix[i][j] = Math.min(matrix[i - 1][j] + 1, matrix[i][j - 1] + 1, matrix[i - 1][j - 1] + cost);
    }
  }
  return matrix[a.length][b.length];
};

/**
 * Enhanced Matching: Checks against master registry AND CRM contacts using Token-Set similarity
 */
const findBestMatch = (name: string, address?: string): { suggestion: string, source: 'MASTER' | 'CRM' | 'INTERNAL', confidence: number } | null => {
  const normName = normalizeText(name);
  if (normName.length < 3) return null;

  const inputTokens = new Set(normName.split(' '));

  let bestMatch = null;
  let bestScore = -1; // Higher is better (0.0 to 1.0)
  let matchSource: 'MASTER' | 'CRM' | 'INTERNAL' = 'INTERNAL';

  // Helper for scoring (Combination of Token Overlap and Levenshtein)
  const calculateScore = (targetName: string, targetAddress?: string) => {
    const normTarget = normalizeText(targetName);
    const targetTokens = new Set(normTarget.split(' '));
    
    // 1. Token Set Ratio
    const intersection = new Set([...inputTokens].filter(x => targetTokens.has(x)));
    const tokenScore = intersection.size / Math.max(inputTokens.size, targetTokens.size);
    
    // 2. Levenshtein Ratio
    const dist = getLevenshteinDistance(normName, normTarget);
    const levScore = 1 - (dist / Math.max(normName.length, normTarget.length));
    
    // 3. Address matching (Bonus)
    let addrScore = 0;
    if (address && targetAddress) {
        const normAddr = normalizeText(address);
        const normTargAddr = normalizeText(targetAddress);
        if (normAddr.includes(normTargAddr) || normTargAddr.includes(normAddr)) {
            addrScore = 0.2; // 20% bonus for address match
        }
    }
    
    // Weighted combination (prefer token overlap for short names with typos)
    const combined = (tokenScore * 0.7) + (levScore * 0.3) + addrScore;
    return Math.min(combined, 1.0);
  };

  // 1. Check against Master Registry
  Object.entries(MASTER_ENTITIES).forEach(([master, details]) => {
    [master, ...details.aliases].forEach(alias => {
        const score = calculateScore(alias, details.address);
        if (score > 0.65 && score > bestScore) {
            bestScore = score;
            bestMatch = master;
            matchSource = 'MASTER';
        }
    });
  });

  // 2. Check against CRM Contacts
  mockContacts.forEach(contact => {
      const score = calculateScore(contact.companyName, contact.notes); 
      if (score > 0.6 && score > bestScore) {
          bestScore = score;
          bestMatch = contact.companyName;
          matchSource = 'CRM';
      }
  });

  if (bestMatch && normalizeText(bestMatch) !== normName) {
      return { suggestion: bestMatch, source: matchSource, confidence: Math.round(bestScore * 100) };
  }

  return null;
};

/**
 * Grade Matcher: Strips prefixes like 'BITUMEN', 'GRADE', 'FRADE' and checks against standard BitumenGrade enum.
 */
const findGradeMatch = (rawGrade: string): string | null => {
    if (!rawGrade || typeof rawGrade !== 'string') return null;
    
    const normalized = rawGrade.toUpperCase()
        .replace(/BITUMEN/g, '')
        .replace(/GRADE/g, '')
        .replace(/FRADE/g, '') // Handle specific user typo
        .trim();
    
    const standardGrades = Object.values(BitumenGrade);
    const match = standardGrades.find(g => g.toUpperCase() === normalized);
    
    if (match && match !== rawGrade) {
        return match;
    }
    
    // If no exact match found but string contains numbers (like 60/70), return normalized version
    if (normalized.match(/\d/)) {
        return normalized;
    }

    return null;
};

const StandardizationModal: React.FC<StandardizationModalProps> = ({ isOpen, onClose, data, onApply }) => {
  const [activeCandidates, setActiveCandidates] = useState<Record<string, string>>({});

  const candidates = useMemo(() => {
    if (!data || !data.imports || !Array.isArray(data.imports)) return [];
    
    const candidatesMap = new Map<string, { current: string, address?: string, suggestion: string, source: string, confidence: number }>();

    data.imports.forEach((m: any) => {
        // Importer check
        if (m.importerName) {
            const match = findBestMatch(m.importerName, m.importerAddress);
            if (match) candidatesMap.set(m.importerName, { current: m.importerName, address: m.importerAddress, ...match });
        }
        // Supplier check
        if (m.supplier) {
            const match = findBestMatch(m.supplier, m.supplierAddress);
            if (match) candidatesMap.set(m.supplier, { current: m.supplier, address: m.supplierAddress, ...match });
        }
        // Notify party check
        if (m.notifyParty) {
            const match = findBestMatch(m.notifyParty);
            if (match) candidatesMap.set(m.notifyParty, { current: m.notifyParty, suggestion: match.suggestion, source: match.source, confidence: match.confidence });
        }
        // Bitumen Grade check
        const gradeVal = m.bitumenGrade || m.grade;
        if (gradeVal) {
            const gradeMatch = findGradeMatch(gradeVal);
            if (gradeMatch) {
                candidatesMap.set(gradeVal, { 
                    current: gradeVal, 
                    suggestion: gradeMatch, 
                    source: 'GRADE', 
                    confidence: 100 
                });
            }
        }
    });

    return Array.from(candidatesMap.values()).sort((a, b) => b.confidence - a.confidence);
  }, [data]);

  useEffect(() => {
    if (isOpen) {
      const initial: Record<string, string> = {};
      candidates.forEach(c => { 
        if (c.confidence >= 70) initial[c.current] = c.suggestion; 
      });
      setActiveCandidates(initial);
    }
  }, [isOpen, candidates]);

  const handleApply = () => {
    if (!data || !data.imports) return;
    const updatedImports = data.imports.map((item: any) => {
      const currentGrade = item.bitumenGrade || item.grade;
      return {
        ...item,
        importerName: activeCandidates[item.importerName] || item.importerName,
        supplier: activeCandidates[item.supplier] || item.supplier,
        notifyParty: activeCandidates[item.notifyParty] || item.notifyParty,
        bitumenGrade: activeCandidates[currentGrade] || currentGrade
      };
    });
    onApply({ ...data, imports: updatedImports });
  };

  const toggleCandidate = (current: string) => {
    setActiveCandidates(prev => {
      const next = { ...prev };
      if (next[current]) delete next[current];
      else {
        const match = candidates.find(c => c.current === current);
        if (match) next[current] = match.suggestion;
      }
      return next;
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-300">
      <div className="bg-white rounded-[32px] shadow-2xl max-w-4xl w-full overflow-hidden border border-slate-200 animate-in zoom-in-95 duration-300 flex flex-col max-h-[90vh]">
        <header className="p-8 bg-indigo-600 text-white flex justify-between items-center shrink-0">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center backdrop-blur-md">
              <ShieldCheck size={24} />
            </div>
            <div>
              <h3 className="text-xl font-black uppercase tracking-tight">Standardization Linker</h3>
              <p className="text-indigo-100 text-xs font-bold opacity-80 uppercase tracking-widest">Cross-Referencing Nexus Master Registry</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-xl transition-colors">
            <X size={24} />
          </button>
        </header>

        <div className="p-8 overflow-y-auto custom-scrollbar space-y-6 flex-grow">
          <div className="bg-indigo-50 border border-indigo-100 p-6 rounded-3xl flex items-start gap-4 shadow-inner">
             <Bot className="text-indigo-600 shrink-0" size={24} />
             <div>
                <p className="text-indigo-900 font-black uppercase tracking-tight text-sm">Token-Set Matching Engine Active</p>
                <p className="text-indigo-700 text-xs leading-relaxed mt-1">
                  Nexus is identifying variations in company names and bitumen grades (e.g., stripping "Bitumen Grade" prefixes) to merge them with your canonical registry. {candidates.filter(c => c.confidence >= 70).length} high-confidence links pre-selected.
                </p>
             </div>
          </div>

          <div className="rounded-3xl border border-slate-200 overflow-hidden shadow-sm bg-slate-50">
            <table className="min-w-full divide-y divide-slate-200">
              <thead className="bg-slate-100/50">
                <tr>
                  <th className="px-6 py-4 text-left text-[10px] font-black text-slate-400 uppercase tracking-widest w-12">Action</th>
                  <th className="px-6 py-4 text-left text-[10px] font-black text-slate-400 uppercase tracking-widest">Parsed Value</th>
                  <th className="px-6 py-4 text-center"></th>
                  <th className="px-6 py-4 text-left text-[10px] font-black text-slate-400 uppercase tracking-widest">Registry Match</th>
                  <th className="px-6 py-4 text-center text-[10px] font-black text-slate-400 uppercase tracking-widest">Score</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {candidates.length > 0 ? candidates.map((c, idx) => (
                  <tr key={idx} className={activeCandidates[c.current] ? 'bg-indigo-50/10' : 'opacity-40 grayscale group'}>
                    <td className="px-6 py-4">
                      <input 
                        type="checkbox" 
                        checked={!!activeCandidates[c.current]}
                        onChange={() => toggleCandidate(c.current)}
                        className="w-5 h-5 text-indigo-600 rounded-lg focus:ring-indigo-500 border-slate-300 cursor-pointer transition-all"
                      />
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-col">
                        <p className="text-sm font-black text-slate-800 uppercase tracking-tight">{c.current}</p>
                        {c.address && (
                          <p className="text-[9px] text-slate-400 flex items-center gap-1 mt-1 font-bold">
                              <MapPin size={10} /> {c.address}
                          </p>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-4 text-center">
                       <ArrowRight size={20} className={activeCandidates[c.current] ? "text-indigo-500" : "text-slate-200"} />
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-black text-indigo-700 uppercase tracking-tight">{c.suggestion}</p>
                        {c.source === 'CRM' && (
                            <span className="flex items-center gap-1 bg-emerald-50 text-emerald-700 border-emerald-100 px-2 py-0.5 rounded text-[9px] font-black border border-emerald-100 uppercase">
                                <Cloud size={10} /> CRM
                            </span>
                        )}
                        {c.source === 'MASTER' && (
                            <span className="bg-indigo-50 text-indigo-500 px-2 py-0.5 rounded text-[9px] font-black border border-indigo-100 uppercase">
                                Registry
                            </span>
                        )}
                        {c.source === 'GRADE' && (
                            <span className="bg-amber-50 text-amber-600 px-2 py-0.5 rounded text-[9px] font-black border border-amber-100 uppercase flex items-center gap-1">
                                <Layers size={10} /> Grade
                            </span>
                        )}
                      </div>
                      <p className="text-[10px] text-slate-400 font-bold uppercase mt-1">Linking to canonical Record</p>
                    </td>
                    <td className="px-6 py-4 text-center">
                        <div className={`inline-flex items-center justify-center w-12 h-12 rounded-full border-4 ${c.confidence >= 80 ? 'border-emerald-100 text-emerald-600' : 'border-amber-100 text-amber-600'} text-[10px] font-black`}>
                            {c.confidence}%
                        </div>
                    </td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan={5} className="px-6 py-16 text-center text-slate-400 italic text-sm">
                      <CheckCircle2 size={48} className="mx-auto mb-4 text-emerald-400 opacity-20" />
                      <p className="font-black uppercase tracking-widest text-[10px]">Registry Fully Standardized</p>
                      <p className="text-slate-400 mt-2">All extracted entities already match canonical records.</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <footer className="p-8 bg-slate-50 border-t border-slate-100 flex justify-between items-center shrink-0">
          <div className="flex items-center gap-3">
             <div className="w-2 h-2 rounded-full bg-indigo-500 animate-pulse"></div>
             <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">Manual review recommended for scores below 70%</span>
          </div>
          <div className="flex gap-3">
            <button 
              onClick={onClose}
              className="px-8 py-3 text-slate-500 font-black text-[10px] uppercase tracking-widest hover:bg-slate-200 rounded-2xl transition-all"
            >
              Skip
            </button>
            <button 
              onClick={handleApply}
              className="px-10 py-3 bg-indigo-600 text-white font-black text-[10px] uppercase tracking-[0.15em] rounded-2xl shadow-xl shadow-indigo-100 hover:bg-indigo-700 transition-all active:scale-95 flex items-center gap-3"
            >
              <Database size={16} /> Link Selected Entities
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default StandardizationModal;